import React from "react";
import { Field, Dropdown, Option } from "@fluentui/react-components";
import { Input } from "#root/components/Input";
import ImageUploader, { UploaderFile } from "#root/components/ImageUploader";
import ButtonGroup from "#root/components/ButtonGroup";
import { stripPhoneNumber, VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import { RESPONDER_ROLES_CONST, FEDERAL_EMPLOYEE_LABEL, STATE_EMPLOYEE_LABEL, LOCAL_EMPLOYEE_LABEL, USR_TEAMS, RESPONDER_STATUS_CONST, RESPONDER_STATUS_FORM } from "#root/utils/responders";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { showToast } from "#root/components/GlobalToaster";
import InfiniteDropdown from "#root/components/InfiniteDropdown";
import { useInfiniteDropdown } from "#root/hooks/useInfiniteDropdown";
import { useInfiniteLocations, useLocationById, Location } from "#root/queries/locations";
import { getEnumLabelByValue } from "#root/utils/enums";
import { handleDateSelection, formatToLocalTime, createLocalDate } from "#root/services/utils";
import { useLocation } from "react-router-dom";  
import './styles.scss';

export interface ResponderFormData {
    firstName: string;
    lastName: string;
    nickname: string;
    departmentOrAgency: string;
    email: string;
    phone: string;
    role?: number;
    usarTeamNumber?: number;
    isTeamLeader?: boolean;
    personalImage?: UploaderFile;
    licenseImage?: UploaderFile;
    incidentNotificationDate?: Date;
    firstTravelDay?: Date;
    checkInDate?: Date;
    firstDayOfWork?: Date;
    anticipatedLastDayOfWork?: Date;
    checkOutDate?: Date;
    locationId?: string;
    status?: number;
    group?: number;
    isIndividualAssignment?: boolean;
}

export const initialResponderFormData: ResponderFormData = {
    firstName: '',
    lastName: '',
    nickname: '',
    departmentOrAgency: '',
    email: '',
    phone: '',
    role: undefined,
    usarTeamNumber: 0,
    isTeamLeader: false,
    personalImage: undefined,
    licenseImage: undefined,
    incidentNotificationDate: undefined,
    firstTravelDay: undefined,
    checkInDate: undefined,
    firstDayOfWork: undefined,
    anticipatedLastDayOfWork: undefined,
    checkOutDate: undefined,
    locationId: undefined,
    status: undefined,
    group: undefined,
    isIndividualAssignment: false
};

interface ResponderFormProps {
    formData: ResponderFormData;
    onFieldChange: (name: string, value: any) => void;
    onSubmit: (formData: ResponderFormData) => Promise<void>;
    isSubmitting: boolean;
    incidentName?: string;
    incidentId?: string;
    isCheckIn?: boolean;
    isChangeStatus?: boolean;
    actions: React.ReactNode;
}

const ResponderForm: React.FC<ResponderFormProps> = ({
    formData,
    onFieldChange,
    onSubmit,
    isSubmitting,
    incidentName,
    incidentId,
    isCheckIn = false,
    isChangeStatus = false,
    actions
}) => {
    const location = useLocation();  
    const isEdit = isCheckIn || isChangeStatus;
    const searchParams = new URLSearchParams(location.search);
    const isQrScaned = searchParams.get('isQrScaned') === 'true';  
    const effectiveIsChangeStatus = isQrScaned ? true : isChangeStatus;  

    const {
        selectedData: selectedLocationData,
        handleChange: handleLocationChange,
        setFilter: setLocationFilter,
        queryResult: locationsQuery
    } = useInfiniteDropdown<Location>({
        initialId: formData.locationId || undefined,
        useInfiniteQuery: useInfiniteLocations,
        useItemById: useLocationById,
        queryParameters: {
            incidentId: incidentId || undefined,
            includeOrganizationLocations: true,
        }
    });

    const handlePersonalImageChange = (files: UploaderFile[]): void => {
        onFieldChange("personalImage", files[0]);
    };

    const handleLicenseImageChange = (files: UploaderFile[]): void => {
        onFieldChange("licenseImage", files[0]);
    };

    const isEmployeeRole = formData.role === FEDERAL_EMPLOYEE_LABEL.value ||
        formData.role === STATE_EMPLOYEE_LABEL.value ||
        formData.role === LOCAL_EMPLOYEE_LABEL.value;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const missingFields = [];

        if (isEmployeeRole) {
            if (!formData.incidentNotificationDate || !formData.firstTravelDay) {
                if (!formData.incidentNotificationDate) missingFields.push("Incident Notification Date");
                if (!formData.firstTravelDay) missingFields.push("First Travel Day");
                if (!formData.usarTeamNumber) missingFields.push("US&R Team");
            }
        }

        if (!formData.personalImage) missingFields.push("Responder Image");

        if (missingFields.length > 0) {
            showToast(`Please fill in the following required fields: ${missingFields.join(", ")}`, 'warning');
            return;
        }

        // Strip spaces from phone number before submitting
        const updatedFormData = {
            ...formData,
            phone: stripPhoneNumber(formData.phone || '')
        };

        // Clear date fields if not an employee role before submitting
        if (!isEmployeeRole) {
            updatedFormData.incidentNotificationDate = undefined;
            updatedFormData.firstTravelDay = undefined;
            updatedFormData.checkInDate = undefined;
            updatedFormData.firstDayOfWork = undefined;
            updatedFormData.anticipatedLastDayOfWork = undefined;
            updatedFormData.checkOutDate = undefined;
        }

        await onSubmit(updatedFormData);
    };

    const formatDate = (value?: Date | string): string => {
        if (!value) return "";
        return formatToLocalTime(value, 'date', false);
    };

    return (
        <form autoComplete="off" onSubmit={handleSubmit} className={`responder-form ${isSubmitting ? 'submitting' : ''}`}>

            <Field label="Incident">
                <Input
                    value={incidentName}
                    disabled
                />
            </Field>

            <h3>Person's Information <div className="divider"></div></h3>

            <div className="field-two-columns">
                <Field label="First Name" required>
                    <Input
                        name="firstName"
                        value={formData.firstName}
                        onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                        maxLength={100}
                        disabled={isEdit}
                    />
                </Field>

                <Field label="Last Name" required>
                    <Input
                        name="lastName"
                        value={formData.lastName}
                        onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                        maxLength={100}
                        disabled={isEdit}
                    />
                </Field>
            </div>

            <Field label="Nickname">
                <Input
                    name="nickname"
                    value={formData.nickname}
                    onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                    maxLength={100}
                    disabled={isEdit}
                />
            </Field>

            <div className="field-three-columns">
                <Field label="Department/Agency" required>
                    <Input
                        name="departmentOrAgency"
                        value={formData.departmentOrAgency}
                        onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                        maxLength={100}
                        disabled={isEdit}
                    />
                </Field>

                <Field label="Email" required>
                    <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                        maxLength={100}
                        disabled={isEdit}
                    />
                </Field>

                <Field label="Phone Number" required>
                    <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => onFieldChange(e.target.name, e.target.value)}
                        pattern={VALIDATION_PATTERNS.PHONE}
                        title={VALIDATION_MESSAGES.PHONE}
                        maxLength={50}
                        disabled={isEdit}
                    />
                </Field>
            </div>

            <Field label="Role" required>
                {isEdit ? (
                    <ButtonGroup
                        name="role"
                        options={formData.role !== undefined ?
                            [RESPONDER_ROLES_CONST.find(r => r.value === formData.role)]
                                .filter(Boolean)
                                .map(e => ({ label: e!.label, value: e!.value }))
                            : []
                        }
                        value={formData.role}
                        onChange={() => { }}
                    />
                ) : (
                    <ButtonGroup
                        name="role"
                        options={RESPONDER_ROLES_CONST.map(e => ({ label: e.label, value: e.value }))}
                        value={formData.role}
                        onChange={(value: string) => onFieldChange("role", value)}
                        required
                    />
                )}
            </Field>

            {isEmployeeRole && (
                <>
                    <h3>Last Day Worked <div className="divider"></div></h3>

                    <div className="field-three-columns">
                        <Field label="Incident Notification Date" required>
                            <DatePicker
                                name="incidentNotificationDate"
                                value={createLocalDate(formData.incidentNotificationDate)}
                                onSelectDate={(date) => handleDateSelection("incidentNotificationDate", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                required
                                disabled={isEdit}
                                allowTextInput
                            />
                        </Field>

                        <Field label="First Travel Day" required>
                            <DatePicker
                                name="firstTravelDay"
                                value={createLocalDate(formData.firstTravelDay)}
                                onSelectDate={(date) => handleDateSelection("firstTravelDay", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                required
                                disabled={isEdit}
                                allowTextInput
                            />
                        </Field>

                        <Field label="Check In Date">
                            <DatePicker
                                name="checkInDate"
                                value={createLocalDate(formData.checkInDate)}
                                onSelectDate={(date) => handleDateSelection("checkInDate", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                allowTextInput
                            />
                        </Field>
                    </div>

                    <div className="field-three-columns">
                        <Field label="First Day of Work">
                            <DatePicker
                                name="firstDayOfWork"
                                value={createLocalDate(formData.firstDayOfWork)}
                                onSelectDate={(date) => handleDateSelection("firstDayOfWork", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                allowTextInput
                            />
                        </Field>

                        <Field label="Anticipated Last Day of Work">
                            <DatePicker
                                name="anticipatedLastDayOfWork"
                                value={createLocalDate(formData.anticipatedLastDayOfWork)}
                                onSelectDate={(date) => handleDateSelection("anticipatedLastDayOfWork", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                allowTextInput
                            />
                        </Field>

                        <Field label="Check Out (Demobilize) Date">
                            <DatePicker
                                name="checkOutDate"
                                value={createLocalDate(formData.checkOutDate)}
                                onSelectDate={(date) => handleDateSelection("checkOutDate", date, onFieldChange)}
                                formatDate={formatDate}
                                className="date-picker"
                                placeholder="MM/DD/YYYY"
                                allowTextInput
                            />
                        </Field>
                    </div>
                </>
            )}

            <div className="field-three-columns">
                <Field label="US&R Team" required>
                    <Dropdown
                        name="usarTeamNumber"
                        value={formData.usarTeamNumber !== undefined ? getEnumLabelByValue(USR_TEAMS, formData.usarTeamNumber) : ''}
                        onOptionSelect={(_, data) => onFieldChange('usarTeamNumber', data.optionValue ? Number(data.optionValue) : undefined)}
                        disabled={isEdit}
                    >
                        {USR_TEAMS.map((team) => (
                            <Option key={team.value} value={team.value.toString()}>
                                {team.label}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                {formData.usarTeamNumber !== undefined 
                && formData.usarTeamNumber !== USR_TEAMS[0].value 
                && !isEdit 
                && (
                    <Field label="Team Leader" required>
                        <ButtonGroup
                            options={[
                                { value: true, label: 'Yes' },
                                { value: false, label: 'No' }
                            ]}
                            onChange={function (value: any): void {
                                onFieldChange('isTeamLeader', value);
                            }}
                            name="isTeamLeader"
                            value={formData.isTeamLeader}
                        />
                    </Field>
                )}
            </div>

            <div className="field-two-columns">
                <Field label="Responder Image" required>
                    <ImageUploader
                        files={formData.personalImage ? [formData.personalImage] : []}
                        onChange={handlePersonalImageChange}
                        maxFiles={1}
                        readOnly={isEdit}
                    />
                </Field>

                <Field label="License / ID">
                    <ImageUploader
                        files={formData.licenseImage ? [formData.licenseImage] : []}
                        onChange={handleLicenseImageChange}
                        maxFiles={1}
                        readOnly={isEdit}
                    />
                </Field>
            </div>

            {isCheckIn && (
                <>
                    <h3>Assign Location<div className="divider"></div></h3>

                    <Field label="Location">
                        <InfiniteDropdown
                            value={formData.locationId}
                            onChange={(_, data) => {
                                handleLocationChange(data.value);
                                onFieldChange("locationId", data.value);
                            }}
                            onFilterChange={setLocationFilter}
                            queryResult={locationsQuery}
                            itemToOption={(location) => ({
                                value: location.id ?? '',
                                text: location.name ?? ''
                            })}
                            placeholder="Select location"
                            selectedItem={selectedLocationData}
                        />
                    </Field>
                </>
            )}

            {effectiveIsChangeStatus && (
                <>
                    <h3>Review/Assign Location & Status <div className="divider"></div></h3>

                    <div className="form-row">
                        <Field label="Assigned Location" required className="span-2">
                            <InfiniteDropdown
                                value={formData.locationId}
                                onChange={(_, data) => {
                                    handleLocationChange(data.value);
                                    onFieldChange("locationId", data.value);
                                }}
                                onFilterChange={setLocationFilter}
                                queryResult={locationsQuery}
                                itemToOption={(location) => ({
                                    value: location.id ?? '',
                                    text: location.name ?? ''
                                })}
                                placeholder="Select location"
                                selectedItem={selectedLocationData}
                                required
                            />
                        </Field>

                        <Field label="Group" required>
                            <Dropdown
                                value={"Not Applicable"}
                                onOptionSelect={(_, data) => onFieldChange('group', Number(data.optionValue))}
                            >
                                <Option key={0} value={"0"}>
                                    Not Applicable
                                </Option>
                            </Dropdown>
                        </Field>

                        <Field label="Status" required>
                            <Dropdown
                                value={formData.status !== undefined ? getEnumLabelByValue(RESPONDER_STATUS_CONST, formData.status) : ''}
                                onOptionSelect={(_, data) => onFieldChange('status', Number(data.optionValue))}
                            >
                                {RESPONDER_STATUS_FORM.map(status => (
                                    <Option key={status.value} value={String(status.value)}>
                                        {status.label}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>

                        <Field label="Individual Assignment" required>
                            <ButtonGroup
                                options={[
                                    { value: true, label: 'Yes' },
                                    { value: false, label: 'No' }
                                ]}
                                onChange={function (value: any): void {
                                    onFieldChange('isIndividualAssignment', value);
                                }}
                                name="isIndividualAssignment"
                                value={formData.isIndividualAssignment}
                            />
                        </Field>
                    </div>
                </>
            )}

            <div className="form-actions">
                {actions}
            </div>
        </form>
    );
};

export default ResponderForm;
